import type { ComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { UserTeam } from './interfaces'

import { useListsStore } from '@shared/model'
import { AppRoute } from '@shared/config'

const mapRecordToOptions = (record: Record<string, string> | null | undefined): AppSelectOption[] => {
  if (!record) return []

  return Object.entries(record).map(([value, text]) => ({ value, text }))
}

interface UserOptionsReturn {
  roleOptions: ComputedRef<AppSelectOption[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  getRoleLabels: (roles: string[]) => string
  getStatusLabel: (status: string) => string
}

/**
 * Role and status labels are backend-owned (`lists.user.*`), so the options and the label lookups
 * both read the hydrated `lists` store — the page enables the `lists` middleware.
 */
export const useUserOptions = (): UserOptionsReturn => {
  const listsStore = useListsStore()

  const roleOptions = computed<AppSelectOption[]>(() => mapRecordToOptions(listsStore.getLists?.user?.roles))
  const statusOptions = computed<AppSelectOption[]>(() => mapRecordToOptions(listsStore.getLists?.user?.status))

  const getRoleLabels = (roles: string[]): string => {
    const map = listsStore.getLists?.user?.roles

    return roles.map(role => map?.[role] ?? role).join(', ')
  }

  const getStatusLabel = (status: string): string => listsStore.getLists?.user?.status?.[status] ?? status

  return { roleOptions, statusOptions, getRoleLabels, getStatusLabel }
}

export const getTeamsLabel = (teams: UserTeam[] | undefined): string =>
  (teams ?? []).map(team => team.name).join(', ')

export const getUserEditPath = (userId: number | string): string => `${AppRoute.Users}/${userId}`

export const toTeamOptions = (teams: UserTeam[]): AppSelectOption[] =>
  teams.map(team => ({ value: String(team.id), text: team.name }))
